import React, { useState } from 'react';
import '../OnboardingWizard.css';
import PasswordInput from '../../../components/PasswordInput';

const BasicInformationComplete = ({ 
  data, 
  onUpdate, 
  onNext, 
  onPrevious, 
  isLoading, 
  error,
  validationErrors = {},
  onPasswordStrengthChange
}) => {
  const [passwordConfirm, setPasswordConfirm] = useState(data.password_confirm || '');
  const [formErrors, setFormErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState({ score: null, isAcceptable: false });

  // Sync password confirmation with data prop
  React.useEffect(() => {
    setPasswordConfirm(data.password_confirm || '');
  }, [data.password_confirm]);

  // Handle input changes
  const handleChange = (field, value) => {
    // Clear errors for this field
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: null }));
    }
    
    onUpdate({ [field]: value });
  };

  // Real-time validation
  const validateField = (field, value) => {
    let error = null;
    
    switch (field) {
      case 'clinic_name':
        if (!value) {
          error = 'Clinic name is required';
        } else if (value.length < 2) {
          error = 'Clinic name must be at least 2 characters';
        } else if (value.length > 100) {
          error = 'Clinic name must be less than 100 characters';
        }
        break;
      case 'password':
        if (!value) {
          error = 'Password is required';
        } else if (value.length < 8) {
          error = 'Password must be at least 8 characters';
        } else if (passwordStrength.score !== null && !passwordStrength.isAcceptable) {
          error = 'Password is too weak. Please choose a stronger password (zxcvbn score 3+ required)';
        }
        // Note: Password strength is validated by zxcvbn in the PasswordInput component
        // Stytch requires zxcvbn score ≥ 3, no LUDS requirements needed
        break;
      case 'password_confirm':
        if (!value) {
          error = 'Please confirm your password';
        } else if (value !== data.password) {
          error = 'Passwords do not match';
        }
        break;
      case 'admin_name':
        if (!value) {
          error = 'Your name is required';
        } else if (value.length < 2) {
          error = 'Name must be at least 2 characters';
        }
        break;
      case 'contact_phone':
        if (value && !/^\+?[\d\s\-\(\)]+$/.test(value)) {
          error = 'Please enter a valid phone number';
        }
        break;
      case 'website_domain':
        if (value && !/^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.?[a-zA-Z]{2,}$/.test(value)) {
          error = 'Please enter a valid domain (e.g., example.com)';
        }
        break;
    }
    
    setFormErrors(prev => ({ ...prev, [field]: error }));
    return error;
  };

  const handleBlur = (field, value) => {
    validateField(field, value);
  };

  // Combine validation errors from backend and frontend
  const allErrors = { ...formErrors, ...validationErrors };

  return (
    <div className="step-content">
      <div className="basic-info-form">
        <div className="onboarding-form">
          
          {/* Email Field - Pre-filled and verified */}
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address <span style={{ color: '#10b981', fontWeight: 'normal' }}>✓ Verified</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={data.email || ''}
              className="form-input verified-field"
              disabled={true}
              autoComplete="email"
              style={{ 
                backgroundColor: '#f9fafb', 
                border: '1px solid #d1d5db',
                color: '#6b7280',
                cursor: 'not-allowed'
              }}
            />
            <small className="form-help">
              ✅ This email has been verified and will be used for your account
            </small>
          </div>

          {/* Clinic Name Field */}
          <div className="form-group">
            <label htmlFor="clinic_name" className="form-label">
              Clinic Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="clinic_name"
              name="clinic_name"
              value={data.clinic_name || ''}
              onChange={(e) => handleChange('clinic_name', e.target.value)}
              onBlur={(e) => handleBlur('clinic_name', e.target.value)}
              className={`form-input ${allErrors.clinic_name ? 'error' : ''}`}
              placeholder="Your Clinic Name"
              disabled={isLoading}
              autoComplete="organization"
            />
            {allErrors.clinic_name && (
              <span className="error-message">{allErrors.clinic_name}</span>
            )}
          </div>

          {/* Password Field */}
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password <span className="required">*</span>
            </label>
            <PasswordInput
              id="password"
              name="password"
              value={data.password || ''}
              onChange={(e) => {
                handleChange('password', e.target.value);
                // Re-validate password confirmation if it exists
                if (passwordConfirm) {
                  validateField('password_confirm', passwordConfirm);
                }
              }}
              onBlur={(e) => handleBlur('password', e.target.value)}
              onStrengthChange={(strength) => {
                setPasswordStrength(strength);
                // Notify parent component
                if (onPasswordStrengthChange) {
                  onPasswordStrengthChange(strength);
                }
                // Re-validate when strength changes
                if (data.password) {
                  validateField('password', data.password);
                }
              }}
              className={`form-input ${allErrors.password ? 'error' : ''}`}
              placeholder="Enter a secure password"
              disabled={isLoading}
              autoComplete="new-password"
              required
              minLength="8"
              showStrengthIndicator={true}
              userInputs={[data.clinic_name, data.email]} // Context for stronger passwords
            />
            {allErrors.password && (
              <span className="error-message">{allErrors.password}</span>
            )}
          </div>

          {/* Password Confirmation Field */}
          <div className="form-group">
            <label htmlFor="password_confirm" className="form-label">
              Confirm Password <span className="required">*</span>
            </label>
            <PasswordInput
              id="password_confirm"
              name="password_confirm"
              value={passwordConfirm}
              onChange={(e) => {
                setPasswordConfirm(e.target.value);
                onUpdate({ password_confirm: e.target.value });
                // Clear error when user starts typing
                if (formErrors.password_confirm) {
                  setFormErrors(prev => ({ ...prev, password_confirm: null }));
                }
              }}
              onBlur={(e) => validateField('password_confirm', e.target.value)}
              className={`form-input ${allErrors.password_confirm ? 'error' : ''}`}
              placeholder="Re-enter your password"
              disabled={isLoading}
              autoComplete="new-password"
              required
              minLength="8"
              confirmValue={data.password}
              showConfirmationStatus={true}
            />
            {allErrors.password_confirm && (
              <span className="error-message">{allErrors.password_confirm}</span>
            )}
          </div>

          {/* Admin Name Field */}
          <div className="form-group">
            <label htmlFor="admin_name" className="form-label">
              Your Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="admin_name"
              name="admin_name"
              value={data.admin_name || ''}
              onChange={(e) => handleChange('admin_name', e.target.value)}
              onBlur={(e) => handleBlur('admin_name', e.target.value)}
              className={`form-input ${allErrors.admin_name ? 'error' : ''}`}
              placeholder="Enter your full name"
              disabled={isLoading}
              autoComplete="name"
            />
            {allErrors.admin_name && (
              <span className="error-message">{allErrors.admin_name}</span>
            )}
            <small className="form-help">
              This will be used for your user account
            </small>
          </div>

          {/* Website Domain Field (Optional) */}
          <div className="form-group">
            <label htmlFor="website_domain" className="form-label">
              Website Domain <span className="optional">(Optional)</span>
            </label>
            <input
              type="text"
              id="website_domain"
              name="website_domain"
              value={data.website_domain || ''}
              onChange={(e) => handleChange('website_domain', e.target.value)}
              onBlur={(e) => handleBlur('website_domain', e.target.value)}
              className={`form-input ${allErrors.website_domain ? 'error' : ''}`}
              placeholder="example.com"
              disabled={isLoading}
              autoComplete="url"
            />
            {allErrors.website_domain && (
              <span className="error-message">{allErrors.website_domain}</span>
            )}
            <small className="form-help">
              Your clinic's website domain
            </small>
          </div>

          {/* Contact Phone Field (Optional) */}
          <div className="form-group">
            <label htmlFor="contact_phone" className="form-label">
              Contact Phone <span className="optional">(Optional)</span>
            </label>
            <input
              type="tel"
              id="contact_phone"
              name="contact_phone"
              value={data.contact_phone || ''}
              onChange={(e) => handleChange('contact_phone', e.target.value)}
              onBlur={(e) => handleBlur('contact_phone', e.target.value)}
              className={`form-input ${allErrors.contact_phone ? 'error' : ''}`}
              placeholder="+1 (555) 123-4567"
              disabled={isLoading}
              autoComplete="tel"
            />
            {allErrors.contact_phone && (
              <span className="error-message">{allErrors.contact_phone}</span>
            )}
          </div>

          {/* Timezone Field */}
          <div className="form-group">
            <label htmlFor="timezone" className="form-label">
              Timezone
            </label>
            <select
              id="timezone"
              name="timezone"
              value={data.timezone || 'Europe/London'}
              onChange={(e) => handleChange('timezone', e.target.value)}
              className="form-select"
              disabled={isLoading}
            >
              <option value="Europe/London">London (GMT/BST)</option>
              <option value="America/New_York">New York (EST/EDT)</option>
              <option value="America/Chicago">Chicago (CST/CDT)</option>
              <option value="America/Denver">Denver (MST/MDT)</option>
              <option value="America/Los_Angeles">Los Angeles (PST/PDT)</option>
              <option value="America/Toronto">Toronto (EST/EDT)</option>
              <option value="Europe/Paris">Paris (CET/CEST)</option>
              <option value="Europe/Berlin">Berlin (CET/CEST)</option>
              <option value="Australia/Sydney">Sydney (AEST/AEDT)</option>
              <option value="Australia/Melbourne">Melbourne (AEST/AEDT)</option>
              <option value="Asia/Tokyo">Tokyo (JST)</option>
              <option value="Asia/Singapore">Singapore (SGT)</option>
            </select>
          </div>

          {/* Currency Field */}
          <div className="form-group">
            <label htmlFor="currency" className="form-label">
              Currency
            </label>
            <select
              id="currency"
              name="currency"
              value={data.currency || 'GBP'}
              onChange={(e) => handleChange('currency', e.target.value)}
              className="form-select"
              disabled={isLoading}
            >
              <option value="GBP">GBP (£) - British Pound</option>
              <option value="USD">USD ($) - US Dollar</option>
              <option value="EUR">EUR (€) - Euro</option>
              <option value="CAD">CAD (C$) - Canadian Dollar</option>
              <option value="AUD">AUD (A$) - Australian Dollar</option>
              <option value="JPY">JPY (¥) - Japanese Yen</option>
              <option value="SGD">SGD (S$) - Singapore Dollar</option>
            </select>
          </div>
        </div>

        {/* Information Panel */}
        <div className="info-panel">
          <div className="info-header">
            <h3>What happens next?</h3>
          </div>
          <div className="info-content">
            <div className="info-step">
              <div className="step-number">1</div>
              <div className="step-text">
                <strong>Connect Pabau</strong>
                <p>Configure your Pabau integration to import data</p>
              </div>
            </div>
            <div className="info-step">
              <div className="step-number">2</div>
              <div className="step-text">
                <strong>Start Your Trial</strong>
                <p>Setup payment for your 14-day free trial</p>
              </div>
            </div>
            <div className="info-step">
              <div className="step-number">3</div>
              <div className="step-text">
                <strong>Access Dashboard</strong>
                <p>Your account will be provisioned and ready to use</p>
              </div>
            </div>
          </div>
          
          <div className="info-features">
            <h4>What you get:</h4>
            <ul>
              <li>✓ 14-day free trial on all plans</li>
              <li>✓ Complete patient management system</li>
              <li>✓ Automated marketing campaigns</li>
              <li>✓ Data import from existing systems</li>
              <li>✓ No setup fees or hidden costs</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicInformationComplete; 